import { Fragment, useContext } from 'react';
import { match } from 'ts-pattern';

import ConfirmModalV2 from '~/domains/common/components/Modal/Confirm/ConfirmModalV2';
import DepositModal from '~/domains/payments/deposits/components/DepositModal';
import DepositCompletedModal from '~/domains/payments/deposits/components/DepositCompletedModal';
import WithdrawalModal from '~/domains/payments/withdrawal/WithdrawalModal';
import ConnectedEnterContestModal from '~/domains/contest/domains/common/components/EnterContestModal/ConnectedEnterContestModal';
import EnterContestSuccessScreenModal from '~/domains/contest/domains/common/components/EnterContestModal/EnterContestSuccessScreenModal/EnterContestSuccessScreenModal';
import DialogContainer from '~/domains/design-system/components/Dialog/DialogContainer';
import {
  AgeRestrictionModal,
  InvalidLocationModal,
  LocationErrorModal,
  LocationPermissionRequiredModal,
  LocationRestrictionModal,
  UserNotVerifiedModal,
  VerifyingDocumentsModal,
} from '~/domains/protectionGuards/Modals';
import EntrantDetailModal from '~/domains/contest/domains/common/components/Entrants/EntrantDetailModal';
import GlossaryModal from '~/domains/common/Modals/GlossaryModal/GlossaryModal';
import PickAlternatesModal from '~/domains/common/Modals/PickAlternatesModal/PickAlternatesModal';
import WelcomeModal from '~/domains/common/components/WelcomeModal/WelcomeModal';
import AutoPickEditSheet from '~/domains/contest/domains/gamecenter/domains/common/components/GamecenterAutoPicks/GamecenterAutoPickEditSheet';
import RAFModal from '~/domains/user/domains/refer-a-friend/components/RAFModal';
import ReviveEntryModal from '~/domains/contest/domains/gamecenter/domains/teamSurvivor/components/ReviveEntryModal/ReviveEntryModal';
import ReviveEntrySuccessModal from '~/domains/contest/domains/gamecenter/domains/teamSurvivor/components/ReviveEntryModal/ReviveEntrySuccessModal';
import NotificationInfoModal from '~/domains/notifications/components/NotificationInfoModal';
import ServiceFeeModal from '~/domains/manage-contest/components/ServiceFeeModal/ServiceFeeModal';
import ConnectedEmailVerificationModal from '~/domains/auth/components/EmailVerificationModal/ConnectedEmailVerificationModal';
import ConnectedProveVerificationModal from '~/domains/auth/components/SignUpSteps/components/ConnectedProveVerificationModal';
import { MarketGroupRulesModal } from '~/domains/market/components/MarketGroupRulesModal/MarketGroupRulesModal';
import EntryOwnershipSheet from '~/domains/contest/domains/gamecenter/domains/common/components/EntryOwnershipSheet/EntryOwnershipSheet';

import useAnimatedDialogModals from '../useAnimatedDialogModals';

import { RootModalsContext } from './RootModalsContext';

function RootModals() {
  const { modals, closeModal, isCloseDisabled } = useContext(RootModalsContext);
  const { modalsCache, activeModal, closingModal } = useAnimatedDialogModals({
    modals,
  });

  return (
    <DialogContainer
      isOpen={!!modals.length}
      onClose={isCloseDisabled ? undefined : closeModal}
      lockScroll
    >
      {modalsCache.map((modal) => {
        const commonProps = {
          onClose: closeModal,
          isCloseDisabled,
          isOpen: activeModal === modal,
          isClosing: closingModal === modal,
        };

        return (
          <Fragment key={modal.type}>
            {match(modal)
              .returnType<React.ReactNode>()
              .with({ type: 'ConfirmationModal' }, (rest) => (
                <ConfirmModalV2 {...rest} {...commonProps} />
              ))
              .with({ type: 'EntryOwnershipSheet' }, (rest) => (
                <EntryOwnershipSheet {...rest} {...commonProps} />
              ))
              .with({ type: 'EnterContestModal' }, (rest) => (
                <ConnectedEnterContestModal {...rest} {...commonProps} />
              ))
              .with({ type: 'EnterContestModalSuccess' }, (rest) => (
                <EnterContestSuccessScreenModal {...rest} {...commonProps} />
              ))
              .with({ type: 'DepositModal' }, (rest) => <DepositModal {...rest} {...commonProps} />)
              .with({ type: 'DepositCompletedModal' }, (rest) => (
                <DepositCompletedModal {...rest} {...commonProps} />
              ))
              .with({ type: 'WithdrawalModal' }, (rest) => (
                <WithdrawalModal {...rest} {...commonProps} />
              ))
              // Protection Guards Modals
              .with({ type: 'AgeRestrictionModal' }, (rest) => (
                <AgeRestrictionModal {...rest} {...commonProps} />
              ))
              .with({ type: 'InvalidLocationModal' }, (rest) => (
                <InvalidLocationModal {...rest} {...commonProps} />
              ))
              .with({ type: 'LocationErrorModal' }, (rest) => (
                <LocationErrorModal {...rest} {...commonProps} />
              ))
              .with({ type: 'LocationPermissionRequiredModal' }, (rest) => (
                <LocationPermissionRequiredModal {...rest} {...commonProps} />
              ))
              .with({ type: 'LocationRestrictionModal' }, (rest) => (
                <LocationRestrictionModal {...rest} {...commonProps} />
              ))
              .with({ type: 'UserNotVerifiedModal' }, (rest) => (
                <UserNotVerifiedModal {...rest} {...commonProps} />
              ))
              .with({ type: 'VerifyingDocumentsModal' }, (rest) => (
                <VerifyingDocumentsModal {...rest} {...commonProps} />
              ))
              .with({ type: 'EntrantDetailModal' }, (rest) => (
                <EntrantDetailModal {...rest} {...commonProps} />
              ))
              .with({ type: 'GlossaryModal' }, (rest) => (
                <GlossaryModal {...rest} {...commonProps} />
              ))
              .with({ type: 'PickAlternatesModal' }, (rest) => (
                <PickAlternatesModal {...rest} {...commonProps} />
              ))
              .with({ type: 'WelcomeModal' }, (rest) => <WelcomeModal {...rest} {...commonProps} />)
              .with({ type: 'AutoPickEditSheet' }, (rest) => (
                <AutoPickEditSheet {...rest} {...commonProps} />
              ))
              .with({ type: 'RAFModal' }, (rest) => <RAFModal {...rest} {...commonProps} />)
              .with({ type: 'ReviveEntryModal' }, (rest) => (
                <ReviveEntryModal {...rest} {...commonProps} />
              ))
              .with({ type: 'ReviveEntrySuccessModal' }, (rest) => (
                <ReviveEntrySuccessModal {...rest} {...commonProps} />
              ))
              .with({ type: 'NotificationInfoModal' }, (rest) => (
                <NotificationInfoModal {...rest} {...commonProps} />
              ))
              .with({ type: 'ServiceFeeModal' }, (rest) => (
                <ServiceFeeModal {...rest} {...commonProps} />
              ))
              .with({ type: 'EmailVerificationModal' }, (rest) => (
                <ConnectedEmailVerificationModal {...rest} {...commonProps} />
              ))
              .with({ type: 'ProveVerificationModal' }, (rest) => (
                <ConnectedProveVerificationModal {...rest} {...commonProps} />
              ))
              .with({ type: 'MarketGroupRulesModal' }, (rest) => (
                <MarketGroupRulesModal {...rest} {...commonProps} />
              ))
              .otherwise(() => null)}
          </Fragment>
        );
      })}
    </DialogContainer>
  );
}

export default RootModals;
